import { Suspense, lazy, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { trialErrorFrom } from '@grove/api-client';
import { apiFetch } from '../lib/api';
import { youtubeVideoId } from '../lib/media';
import { useMediaProgress } from '../lib/useMediaProgress';
import { ArticleBody } from './ArticleBody';
import { VideoPlayer, VideoPlayerHandle } from './VideoPlayer';
import { TranscriptPanel } from './TranscriptPanel';
import type { Article } from './ArticleCard';

// pdfjs is heavy — load it only when a pdf article actually opens.
const PdfViewer = lazy(() => import('./PdfViewer').then((m) => ({ default: m.PdfViewer })));

const API = import.meta.env.VITE_API_URL ?? 'https://api.grovereader.com';

interface FullArticle extends Article {
  mediaPosition?: number | null;
  mediaDuration?: number | null;
  transcript?: Array<{ start: number; dur: number; text: string }> | null;
}

interface Props {
  article: FullArticle;
  domain?: string;
  /** Text-selection highlights — article type only. */
  onMouseUp?: () => void;
}

/**
 * The reader body, branched by content type. Shared by the full-page reader
 * and the Queue split-pane panel so the branch logic can't drift: video →
 * player + synced transcript, pdf → in-app viewer, article → markdown body.
 */
export function MediaBody({ article, domain, onMouseUp }: Props) {
  const type = article.type ?? 'article';
  if (type === 'video') return <VideoBody article={article} />;
  if (type === 'pdf') return <PdfBody article={article} />;
  return (
    <>
      <ReextractBanner article={article} />
      <ArticleBody article={article} domain={domain} onMouseUp={onMouseUp} />
    </>
  );
}

function VideoBody({ article }: { article: FullArticle }) {
  const playerRef = useRef<VideoPlayerHandle>(null);
  const [currentTime, setCurrentTime] = useState(article.mediaPosition ?? 0);
  const { report, flush } = useMediaProgress({ articleId: article.id, status: article.status });
  const videoId = youtubeVideoId(article.url);

  if (!videoId) {
    // A video row whose id we can't parse client-side — degrade to the markdown body.
    return <ArticleBody article={article} />;
  }

  // Don't restore position on finished articles — same contract as scroll restore.
  const finished = article.status === 'archived' || article.status === 'filed';
  const startSeconds = finished ? 0 : article.mediaPosition ?? 0;

  return (
    <div>
      <VideoPlayer
        ref={playerRef}
        videoId={videoId}
        startSeconds={startSeconds}
        onTime={(t) => {
          setCurrentTime(t);
          report(t);
        }}
        onPause={(t) => {
          setCurrentTime(t);
          report(t);
          flush();
        }}
      />
      <TranscriptPanel
        segments={article.transcript}
        currentTime={currentTime}
        onSeek={(s) => {
          playerRef.current?.seekTo(s);
          setCurrentTime(s);
        }}
      />
    </div>
  );
}

function PdfBody({ article }: { article: FullArticle }) {
  const { report } = useMediaProgress({ articleId: article.id, status: article.status });
  const [fileFailed, setFileFailed] = useState(false);
  const finished = article.status === 'archived' || article.status === 'filed';

  // No stored binary (upload failed / R2 unconfigured / expired source) —
  // fall back to the extracted text so the save is still readable.
  if (fileFailed) {
    return <ArticleBody article={article} />;
  }

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      <PdfViewer
        fileUrl={`${API}/api/articles/${article.id}/file`}
        startPage={finished ? 1 : article.mediaPosition ?? 1}
        onPageChange={(page) => report(page)}
        onLoadError={() => setFileFailed(true)}
      />
    </Suspense>
  );
}

/**
 * Old stubs: an article-typed row whose URL is actually a video or PDF
 * (saved before content types existed). Offer a one-click re-extract that
 * re-runs the media pipeline in place.
 */
function ReextractBanner({ article }: { article: FullArticle }) {
  const queryClient = useQueryClient();
  const isVideo = !!youtubeVideoId(article.url);
  const isPdf = /\.pdf$/i.test(safePathname(article.url));

  const reextract = useMutation({
    mutationFn: () => apiFetch(`/articles/${article.id}/reextract`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article', article.id] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });

  // Filed articles are released/read-only — never offer a mutating re-extract.
  if ((!isVideo && !isPdf) || article.status === 'filed') return null;

  const trialExpired = reextract.isError ? trialErrorFrom(reextract.error) : null;
  if (trialExpired) {
    return (
      <div className="rounded-xl mb-6 p-4 flex items-center justify-between gap-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
        <p className="text-sm" style={{ color: '#FBBF24' }}>
          Your trial has ended — subscribe to re-extract this {isVideo ? 'video' : 'PDF'}.
        </p>
        <Link
          to="/settings"
          className="text-xs px-3 py-1.5 rounded-lg font-semibold flex-shrink-0"
          style={{ background: 'rgba(245,158,11,0.15)', color: '#FBBF24', border: '1px solid rgba(245,158,11,0.3)' }}
        >
          Subscribe
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-xl mb-6 p-4 flex items-center justify-between gap-3" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)' }}>
      <p className="text-sm" style={{ color: '#C4B5FD' }}>
        This looks like a {isVideo ? 'video' : 'PDF'} — re-extract it to get the in-app {isVideo ? 'player and transcript' : 'viewer'}.
      </p>
      <button
        onClick={() => reextract.mutate()}
        disabled={reextract.isPending}
        className="text-xs px-3 py-1.5 rounded-lg font-semibold flex-shrink-0"
        style={{ background: 'rgba(139,92,246,0.15)', color: '#C4B5FD', border: '1px solid rgba(139,92,246,0.3)' }}
      >
        {reextract.isPending ? 'Re-extracting…' : `Re-extract as ${isVideo ? 'video' : 'PDF'}`}
      </button>
    </div>
  );
}

function safePathname(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return '';
  }
}
